import {
  Container,
  Grid,
  LinearProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import { GroupsContainer, OutlinedIconButton } from 'shared/components/styled';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Root, useStyles } from './styled';
import VirtualizedView, { RowGrid } from 'shared/components/VirtualizedView';
import GlobalResource from 'shared/components/GlobalResources';
import { columnTypes } from 'shared/components/tables';
import { debounce, uniq, uniqBy } from 'lodash';
import { searchToQuery } from 'shared/components/makeNav';
import { useHistory, useLocation, Route, Redirect } from 'react-router-dom';

import { useServiceByIdQuery } from '../ServiceDrawer/useServiceByIdQuery';
import {
  useIndicatorBenchmarks,
  useIndicatorMeasurementType,
  useIndicatorServices,
  useIndicatorStage,
  useIndicatorTags,
  useNewIndicator,
  useUpdateIndicator,
  useMergeIndicator,
  useDeleteIndicator
} from 'shared/apollo';

import { Add as AddIcon } from '@mui/icons-material';
import Autocomplete from '@mui/lab/Autocomplete';
import IndicatorDrawer from '../IndicatorDrawer/IndicatorDrawer';
import { DetailPopup, ConfirmationDialog } from 'shared/components';
import MergeDrawer from '../MergeDrawer';
import ResetIcon from '@mui/icons-material/Restore';
import { SearchInputContainer } from 'shared/components/SearchInput';
import get from 'lodash/get';
import { tableCols } from './constants';
import useHash from 'shared/utils/useHash';
import { useIndicatorsQuery } from './useIndicatorsQuery';
import { useUser } from 'shared/auth';
import { HomePage, PageTop, HomeButton, MainButton, ButtonSpacer } from './styled';
import GlobalResourceDropDown from 'shared/account/components/GlobalResourceDropDown';
import { useServiceCollectionsQuery } from '../ServiceCollections/useServiceCollectionsQuery';
import { serviceByIdQuery } from '../ServiceDrawer/serviceByIdQuery';

const baseUrl = '/account/indicators';
const storageKey = 'indicatorsUrl';

export default function Indicators() {
  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const userId = effectiveUser?.attributes['custom:user_id'];
  const defaultUrl = userId && localStorage.getItem(`User${userId}.${storageKey}`);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  return (
    <Route
      path={baseUrl}
      component={IndicatorsMain}
    >
      {isFirstLoad && defaultUrl &&
        <Redirect to={defaultUrl} />}
    </Route>
  );
}

function IndicatorsMain() {
  const classes = useStyles();
  const { data, isLoading, refresh } = useIndicatorsQuery();
  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const userId = effectiveUser?.attributes['custom:user_id'];
  const { search: searchParams, pathname } = useLocation();
  const [selection, setSelection] = useState([]);

  /* drawer requirements */
  const [selectedIndicator, setSelectedIndicator] = useState();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mergeDrawerOpen, setMergeDrawerOpen] = useState(false);
  const { data: stages } = useIndicatorStage();
  const { data: tags } = useIndicatorTags();
  const { data: benchmarkTypes } = useIndicatorBenchmarks();
  const { data: measurementTypes } = useIndicatorMeasurementType();
  const { data: services } = useIndicatorServices();
  const { run: runNew, isLoading: loadingNew } = useNewIndicator();
  const { run: runUpdate, isLoading: loadingUpdate } = useUpdateIndicator();
  const { run: runMerge, isLoading: loadingMerge } = useMergeIndicator();
  const { run: runDelete, isLoading: loadingDelete } = useDeleteIndicator();
  const drawerProps = { tags, services, benchmarkTypes, measurementTypes, stages };
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState();

  const onSave = (values, resetForm) => {
    const { indicators, ...form } = values;
    const isMulti = Array.isArray(form.id);
    if (isMulti) {
      let done = 0;
      indicators.forEach(({ dbId: id, name, definition, stageId, guidance }) => {
        const multiForm = {
          id,
          name,
          definition,
          stageId,
          guidance,
          tagList: form.tagList,
          servicesList: form.servicesList,
          measurementTypeId: form.measurementTypeId,
          benchmarkType: form.benchmarkType
        };
        setSelectedIndicator();
        setDrawerOpen(false);
        runUpdate(multiForm, () => {
          done += 1;
          if (done === indicators.length) {
            resetForm();
            refresh();
          }
        });
      });
    } else {
      if (form.id) {
        setSelectedIndicator();
        setDrawerOpen(false);
        runUpdate(form, () => {
          resetForm();
          refresh();
        });
      } else {
        setSelectedIndicator();
        setDrawerOpen(false);
        runNew(form, (newId) => {
          resetForm();
          refresh();
        });
      }
    }
  };

  const handleOnMerge = (form, resetForm) => {
    setMergeDrawerOpen(false);
    runMerge(form, () => {
      resetForm();
      refresh();
    });
    setSelectedIndicator();
  };

  const onClose = () => {
    setSelectedIndicator();
    setDrawerOpen(false);
    refresh();
  };

  const onAddNew = () => {
    setSelectedIndicator();
    setDrawerOpen(true);
  };

  const handleViewDetails = (item) => {
    setSelectedIndicator(item);
    setDrawerOpen(true);
  };


  const prepareMultiEdit = (items) => {
    const tags = [];
    const services = [];
    items.forEach(indicator => {
      indicator.tags.forEach(tag => {
        tags.push(tag);
      });
      indicator.services.forEach(service => {
        services.push(service);
      });
    });
    const selectedMulti = {
      id: items.map(i => i.dbId),
      name: '',
      stageId: null,
      definition: '',
      guidance: '',
      benchmarkType: items[0].benchmarkType,
      measurementTypeId: items[0].measurementTypeId,
      tags: uniqBy(tags, 'tag'),
      services: uniqBy(services, 'dbId'),
      indicators: items
    };
    setSelectedIndicator(selectedMulti);
  };

  const onEdit = () => {
    if (selection) {
      if (selection.length === 0) {
        setSelectedIndicator();
      } else if (selection.length === 1) {
        const updatedIndicator = data.filter(item => item.dbId === selection[0].dbId)[0];
        setSelectedIndicator(updatedIndicator);
      } else if (selection.length > 1 && selection.length <= 10) {
        prepareMultiEdit(selection);
      }
      if (selection.length <= 10) {
        setDrawerOpen(true);
      }
    }
  };

  // Virtualized View Compatible onMerge
  // const onMerge = () => {
  //   if (selection) {
  //     setMergeDrawerOpen(true)
  //   }
  // }

  const onMerge = (select) => {
    setSelectedIndicator(select);
    setMergeDrawerOpen(true);
  };

  const onRequestDelete = (selected) => {
    const markedServices = (selected.dbId) ? [selected.dbId] : selected.id;
    setIdToDelete(markedServices);
    setDeleteDialogOpen(true);
  };

  const prepareDelete = (selected) => {
    const markedServices = selected.map(({ dbId }) => dbId);
    setIdToDelete(markedServices);
    setDeleteDialogOpen(true);
  };

  const handleDelete = (selected) => {
    setIdToDelete(selected.map(item => item.dbId));
    setDeleteDialogOpen(true);
  };

  const onDelete = () => {
    setDrawerOpen(false);
    setDeleteDialogOpen(false);
    let processed = 0;
    idToDelete.map(id => {
      runDelete(({ id }), () => {
        processed += 1;
        if (processed === idToDelete.length) {
          refresh();
          setIdToDelete();
        }
      });
    });
  };
  /* end of drawer requirements */

  const handleFilterChange = useCallback(() => {
    const { pathname, search } = window.location;
    const key = `User${userId}.${storageKey}`;
    localStorage.setItem(key, `${pathname}${search}`);
  }, [userId]);
  useEffect(handleFilterChange, [searchParams, pathname, userId]);
  const { push } = useHistory();
  const handleClearPageSettings = useCallback(() => {
    const key = `User${userId}.${storageKey}`;
    localStorage.removeItem(key);
    push(baseUrl);
  }, [userId, push]);

  const [filter, setFilter] = useState();

  const groups = useMemo(() => data
    ?.filter(obj => !filter || matchQuickFilter(obj, filter))
    , [data]);
  const [columns, setColumns] = useState(tableCols);
  const hash = useHash() || '';
  const hashParts = hash.substring(1).split('/').filter(part => part);

  const handleRenderRow = useCallback(({
    value,
    columns,
    style, 
    selectedRows,
    setSelectedRows,
    hasCheckBox,
    isLastRow,
  }) => {
    return (
      <RowGrid
        {...{
          style, value, columns, columnTypes,
          selectedRows, setSelectedRows, hasCheckBox,
          isLastRow,
        }}
        keyField="dbId"
      />
    );
  }, [columns]);

  const searchOptions = data && uniq(
    quickFilterFields
      .reduce((all, field) => [
        ...all,
        ...data.map(row => get(row, field))
      ], [])
  )
    .filter(option => option)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);
  const handleSearchBlur = useCallback(() => {
    setIsSearchFocused(false);
  }, []);
  const debouncedSetFilter = useMemo(() => debounce(setFilter, 500), []);
  const handleSearchInput = useCallback((event, value) => {
    debouncedSetFilter(value);
  }, [debouncedSetFilter]);
  const handleSearchRenderInput = useCallback(params => (
    <SearchInputContainer maxWidth={400}>
      <TextField
        placeholder={isSearchFocused ? undefined : 'Search indicators'}
        onFocus={handleSearchFocus}
        onBlur={handleSearchBlur}
        {...params}
      />
    </SearchInputContainer>
  ), [handleSearchInput, isSearchFocused]);
  return (<Fragment>
    {/* <Rows
      onFilterChange={handleFilterChange}
      {...{
        isLoading: (isLoading || loadingNew || loadingUpdate),
        columns,
        searchOptions, handleSearchInput, 
        handleSearchRenderInput, groups, 

        handleClearPageSettings, refresh,
        handleRenderRow, setSelection,
        selection, onAddNew, onEdit, onMerge, prepareDelete
      }}
    /> */}
    <GlobalResource
      resource='Indicator'
      fields={['dbId', 'name', 'stgName', 'tags']}
      fieldTitles={['ID', 'Name', 'Stage', 'Tags']}
      onAddNew={onAddNew}
      handleViewDetails={handleViewDetails}
      {...{
        data,
        onMerge,
        handleDelete,
        isLoading: (isLoading || loadingNew || loadingUpdate),
      }}
    />
    <DetailPopup title="Indicator" open={drawerOpen} onClose={onClose} insideCard={false} width={'450px'}>
      {drawerOpen && (
        <Container maxWidth={false}>
          <IndicatorDrawer {...drawerProps} onSave={onSave} selected={selectedIndicator} requestDelete={onRequestDelete} />
        </Container>
      )}
    </DetailPopup>
    <DetailPopup
      title="Merge Indicators"
      open={mergeDrawerOpen}
      onClose={() => setMergeDrawerOpen(false)}
      insideCard={false}
      width={'450px'}
    >
      {mergeDrawerOpen && (
        <Container maxWidth={false}>
          <MergeDrawer onMerge={handleOnMerge} selected={selectedIndicator} indicators={data} />
        </Container>
      )}
    </DetailPopup>
    <ConfirmationDialog
      openDialog={deleteDialogOpen}
      onCloseDialog={() => setDeleteDialogOpen(false)}
      actionButtonOne={onDelete}
      actionButtonTwo={() => setDeleteDialogOpen(false)}
      textButtonOne="Delete"
      textButtonTwo="Cancel"
      title="Are you sure you want to delete the indicator selected?"
    />
  </Fragment>);
}

// const Rows = memo(({
//   isLoading, searchOptions, handleSearchInput,
//   handleSearchRenderInput, groups,
//   columns,
//   handleClearPageSettings,
//   handleRenderRow, setSelection, onFilterChange,
//   selection, onAddNew, onEdit, onMerge, prepareDelete,
// }) => {
//   const [filters, setFilters] = useState([])
//   const handleFilterValue = useCallback(() => {
//     try {
//       const { search } = window.location
//       const query = searchToQuery(search)
//       setFilters([
//         'name', 'stgName', 'definition', 'guidance',
//         'benchmarkType', 'measurementType',
//       ]
//         .filter(name => query[name])
//         .map(name => [name, query[name]])
//         .reduce((all, [name, values]) => [
//           ...all,
//           ...JSON.parse(values)
//             .map(value => ({ filter: name, value }))
//         ], [])
//       )
//       onFilterChange()
//     } catch (err) {
//       alert(err)
//     }
//   }, [onFilterChange])
//   const filtered = groups
//     ?.filter(obj => !filters.length || filters
//       .some(({ filter, value }) => get(obj, filter) === value))

//   const virtualized = useRef()
//   useEffect(() => {
//     if (!virtualized) {
//       return
//     }
//     virtualized.current?.clearSelection()
//   }, [])

//   return (
//     <HomePage noPadding fullHeight>
//       <PageTop>
//         {isLoading && <LinearProgress variant="query" />}
//         <Grid
//           container
//           direction="row"
//           alignItems="center"
//           xs={12}
//         >
//           <Grid item xs={3}>
//             <Autocomplete
//               id="indicator-search"
//               freeSolo
//               options={searchOptions}
//               renderInput={handleSearchRenderInput}
//               onInputChange={handleSearchInput}
//             />
//           </Grid>
//           <Grid item xs={7}>
//             <Tooltip title="Reset filters" arrow>
//               <OutlinedIconButton
//                 onClick={handleClearPageSettings}
//               >
//                 <ResetIcon />
//               </OutlinedIconButton>
//             </Tooltip>
//             <ButtonSpacer withBorder />
//             {selection && selection.length > 0 && (
//               <HomeButton onClick={onEdit} disabled={(selection.length > 10)}>
//                 Edit
//               </HomeButton>
//             )}
//             {selection && selection.length > 1 && (
//               <HomeButton onClick={onMerge}>
//                 Merge
//               </HomeButton>
//             )}
//             {selection && selection.length > 0 && (
//               <HomeButton onClick={() => { prepareDelete(selection) }}>
//                 Delete
//               </HomeButton>
//             )}
//           </Grid>
//           <Grid item xs={2} container justifyContent="flex-end">
//             <MainButton
//               onClick={onAddNew}
//               startIcon={<AddIcon />}
//             >
//               Add Indicator
//             </MainButton>
//           </Grid>
//         </Grid>
//         <GlobalResourceDropDown value="Indicators" />
//       </PageTop>
//       <Root>
//         <GroupsContainer>
//           <VirtualizedView
//             ref={virtualized}
//             view="grid"
//             groups={filtered}
//             unfilteredGroups={groups}
//             {...{ columns, isLoading }}
//             onRenderRow={handleRenderRow}
//             onSelectionChanged={setSelection}
//             onFilterValue={handleFilterValue}
//           />
//         </GroupsContainer>
//       </Root>
//     </HomePage>
//   )
// })


const quickFilterFields = [
  'name',
  'stgName',
];

function matchQuickFilter(obj, filter) {
  filter = filter.toLowerCase();
  return quickFilterFields
    .some(field => String(get(obj, field)).toLowerCase().includes(filter));
}
